import { pausableWatch, type WatchPausableReturn } from "@vueuse/core";
import { toRef, type Ref } from "vue";
import { injectDataCollectionContext } from "./data-collection";
import type { DataCollectionState } from "./data-collection-state";

export function useDataCollectionStateModel<
	TState extends keyof DataCollectionState,
	TValue extends DataCollectionState[TState],
	TEmit extends (evt: `update:${TState}`, value: TValue) => void,
>(
	name: TState,
	props: Partial<Pick<DataCollectionState, TState>>,
	emit: TEmit,
	state?: DataCollectionState
) {
	if (state == null) {
		const ctx = injectDataCollectionContext();
		state = ctx.state;
	}

	const watchers: WatchPausableReturn[] = [];

	watchers.push(
		pausableWatch(
			() => props[name] as TValue | undefined,
			(data: TValue | undefined) => {
				if (data === undefined) {
					return;
				}

				watchers.forEach((w) => w.pause());
				state[name] = data;
				watchers.forEach((w) => w.resume());
			},
			{ immediate: true }
		)
	);

	watchers.push(
		pausableWatch(
			() => state[name] as TValue,
			(state: TValue) => {
				watchers.forEach((w) => w.pause());
				emit(`update:${name}`, state);
				watchers.forEach((w) => w.resume());
			}
		)
	);

	return toRef(state, name) as Ref<TValue>;
}
